import {
  IBlueprint,
  BlueprintType,
  BlueprintCategory,
  BlueprintParameter,
  BlueprintParameterGroup,
  BlueprintMetadata,
  BlueprintGenerationContext,
  BlueprintGenerationResult
} from '../types/blueprint-interfaces';
import { ICSElement } from '../types/interfaces';

/**
 * Базовая реализация шаблона
 */
export class Blueprint implements IBlueprint {
  public readonly id: string;
  public readonly name: string;
  public readonly description: string;
  public readonly type: BlueprintType;
  public readonly category: BlueprintCategory;
  public readonly version: string;
  public readonly metadata: BlueprintMetadata;
  public readonly parameterGroups: BlueprintParameterGroup[];

  private readonly generator: (context: BlueprintGenerationContext) => Promise<BlueprintGenerationResult>;

  constructor(config: {
    id: string;
    name: string;
    description: string;
    type: BlueprintType;
    category: BlueprintCategory;
    version?: string;
    metadata?: BlueprintMetadata;
    parameterGroups?: BlueprintParameterGroup[];
    generator: (context: BlueprintGenerationContext) => Promise<BlueprintGenerationResult>;
  }) {
    this.id = config.id;
    this.name = config.name;
    this.description = config.description;
    this.type = config.type;
    this.category = config.category;
    this.version = config.version || '1.0.0';
    this.metadata = {
      created: new Date(),
      updated: new Date(),
      ...config.metadata
    };
    this.parameterGroups = config.parameterGroups || [];
    this.generator = config.generator;
  }

  /**
   * Получение всех параметров в плоском списке
   */
  get parameters(): BlueprintParameter[] {
    return this.parameterGroups.flatMap(group => group.parameters);
  }

  /**
   * Валидация параметров
   */
  validateParameters(parameters: Record<string, any>): {
    valid: boolean;
    errors: Array<{ parameter: string; message: string }>;
    warnings: Array<{ parameter: string; message: string }>;
  } {
    const errors: Array<{ parameter: string; message: string }> = [];
    const warnings: Array<{ parameter: string; message: string }> = [];

    for (const param of this.parameters) {
      const value = parameters[param.id];

      // Проверка обязательных параметров
      if (param.required && (value === undefined || value === null)) {
        errors.push({
          parameter: param.id,
          message: `Параметр '${param.name}' является обязательным`
        });
        continue;
      }

      // Пропустить валидацию если параметр не задан и не обязательный
      if (value === undefined || value === null) {
        continue;
      }

      // Проверка типов
      if (!this.validateParameterType(param, value)) {
        errors.push({
          parameter: param.id,
          message: `Параметр '${param.name}' должен быть типа ${param.type}`
        });
        continue;
      }

      // Проверка зависимостей
      if (param.dependencies) {
        for (const depId of param.dependencies) {
          if (!parameters.hasOwnProperty(depId)) {
            warnings.push({
              parameter: param.id,
              message: `Параметр '${param.name}' зависит от '${depId}', который не задан`
            });
          }
        }
      }

      // Проверка условий отображения
      if (param.condition && !param.condition(parameters)) {
        warnings.push({
          parameter: param.id,
          message: `Параметр '${param.name}' не должен быть задан при текущих условиях`
        });
      }

      // Валидация значений
      if (param.validation) {
        const validationResult = this.validateParameterValue(param, value);
        if (validationResult !== true) {
          errors.push({
            parameter: param.id,
            message: validationResult
          });
        }
      }

      // Проверка опций для select/multiselect
      if ((param.type === 'select' || param.type === 'multiselect') && param.options) {
        const validValues = param.options.map(opt => opt.value);
        if (param.type === 'select') {
          if (!validValues.includes(value)) {
            errors.push({
              parameter: param.id,
              message: `Значение '${value}' не входит в список допустимых значений`
            });
          }
        } else if (param.type === 'multiselect') {
          if (!Array.isArray(value) || !value.every(v => validValues.includes(v))) {
            errors.push({
              parameter: param.id,
              message: `Все значения должны входить в список допустимых значений`
            });
          }
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Валидация типа параметра
   */
  private validateParameterType(param: BlueprintParameter, value: any): boolean {
    switch (param.type) {
      case 'string':
        return typeof value === 'string';
      case 'number':
        return typeof value === 'number' && !isNaN(value);
      case 'boolean':
        return typeof value === 'boolean';
      case 'array':
        return Array.isArray(value);
      case 'object':
        return typeof value === 'object' && value !== null && !Array.isArray(value);
      case 'select':
        return true; // Проверяется отдельно через options
      case 'multiselect':
        return Array.isArray(value);
      default:
        return true;
    }
  }

  /**
   * Валидация значения параметра
   */
  private validateParameterValue(param: BlueprintParameter, value: any): string | true {
    const validation = param.validation!;

    // Числовые ограничения
    if (typeof value === 'number') {
      if (validation.min !== undefined && value < validation.min) {
        return `Значение должно быть не менее ${validation.min}`;
      }
      if (validation.max !== undefined && value > validation.max) {
        return `Значение должно быть не более ${validation.max}`;
      }
    }

    // Строковые ограничения
    if (typeof value === 'string') {
      if (validation.min !== undefined && value.length < validation.min) {
        return `Длина строки должна быть не менее ${validation.min} символов`;
      }
      if (validation.max !== undefined && value.length > validation.max) {
        return `Длина строки должна быть не более ${validation.max} символов`;
      }
      if (validation.pattern) {
        const regex = new RegExp(validation.pattern);
        if (!regex.test(value)) {
          return `Значение не соответствует требуемому формату`;
        }
      }
    }

    // Массивы
    if (Array.isArray(value)) {
      if (validation.min !== undefined && value.length < validation.min) {
        return `Количество элементов должно быть не менее ${validation.min}`;
      }
      if (validation.max !== undefined && value.length > validation.max) {
        return `Количество элементов должно быть не более ${validation.max}`;
      }
    }

    // Пользовательская валидация
    if (validation.custom) {
      const result = validation.custom(value);
      if (result !== true) {
        return typeof result === 'string' ? result : 'Значение не прошло пользовательскую валидацию';
      }
    }

    return true;
  }

  /**
   * Получение значений параметров по умолчанию
   */
  getDefaultParameters(): Record<string, any> {
    const defaults: Record<string, any> = {};
    
    for (const param of this.parameters) {
      if (param.defaultValue !== undefined) {
        defaults[param.id] = param.defaultValue;
      }
    }

    return defaults;
  }

  /**
   * Генерация структуры на основе параметров
   */
  async generate(context: BlueprintGenerationContext): Promise<BlueprintGenerationResult> {
    const startTime = Date.now();
    const startMemory = process.memoryUsage().heapUsed;

    try {
      // Валидация параметров если включена
      if (context.options?.validateParameters !== false) {
        const validation = this.validateParameters(context.parameters);
        if (!validation.valid) {
          throw new Error(`Ошибки валидации параметров: ${validation.errors.map(e => e.message).join(', ')}`);
        }
      }

      // Генерация структуры
      const result = await this.generator(context);

      // Добавление статистики
      const endTime = Date.now();
      const endMemory = process.memoryUsage().heapUsed;

      result.statistics = {
        totalElements: result.elements.length,
        generationTime: endTime - startTime,
        memoryUsed: endMemory - startMemory
      };

      return result;
    } catch (error) {
      throw new Error(`Ошибка генерации шаблона '${this.name}': ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Предварительный просмотр структуры
   */
  async preview(parameters: Record<string, any>): Promise<{
    structure: string;
    elementCount: number;
    estimatedSize: number;
  }> {
    try {
      // Выполняем dry-run генерацию
      const result = await this.generate({
        parameters,
        options: { dryRun: true, validateParameters: true }
      });

      // Создаем текстовое представление структуры
      const structure = this.generateStructureText(result.elements, result.rootElement);
      
      return {
        structure,
        elementCount: result.elements.length,
        estimatedSize: JSON.stringify(result.elements).length
      };
    } catch (error) {
      return {
        structure: `Ошибка предварительного просмотра: ${error instanceof Error ? error.message : String(error)}`,
        elementCount: 0,
        estimatedSize: 0
      };
    }
  }

  /**
   * Генерация текстового представления структуры
   */
  private generateStructureText(elements: ICSElement[], rootElement?: ICSElement): string {
    if (elements.length === 0) {
      return 'Пустая структура';
    }

    const lines: string[] = [];
    const elementMap = new Map<string, ICSElement>();
    
    // Создаем карту элементов
    for (const element of elements) {
      elementMap.set(element.id, element);
    }

         // Функция для рекурсивного обхода
     const traverse = (element: ICSElement, depth: number = 0) => {
       const indent = '  '.repeat(depth);
       const childrenCount = element.elementsCount();
       const childrenInfo = childrenCount > 0 ? ` (${childrenCount} детей)` : '';
       lines.push(`${indent}${element.name || element.id}${childrenInfo}`);

       // Обходим все дочерние элементы
       const allElements = element.getAllElements();
       for (const child of allElements) {
         if ('addElement' in child) { // Проверяем что это ICSElement
           traverse(child as ICSElement, depth + 1);
         }
       }
     };

     // Начинаем с корневого элемента или ищем элементы верхнего уровня
     if (rootElement) {
       traverse(rootElement);
     } else {
       // Обходим все элементы как отдельные корневые
       for (const element of elements) {
         traverse(element);
       }
     }

    return lines.join('\n');
  }

  /**
   * Клонирование шаблона с модификациями
   */
  clone(modifications?: Partial<IBlueprint>): IBlueprint {
    return new Blueprint({
      id: modifications?.id || this.id,
      name: modifications?.name || this.name,
      description: modifications?.description || this.description,
      type: modifications?.type || this.type,
      category: modifications?.category || this.category,
      version: modifications?.version || this.version,
      metadata: { ...this.metadata, ...modifications?.metadata },
      parameterGroups: modifications?.parameterGroups || this.parameterGroups,
      generator: this.generator
    });
  }
} 